/**
 * THE SETTINGS WHEEL — top right, between the language and «Log out».
 *
 * Gerónimo (29 Oct 2026): «un icono de rueda de settings para configurar el
 * tamaño de la letra, las conexiones — si estás conectado a Google, si está
 * sync, a HubSpot — que todo se maneje ahí». What a person sets here is
 * theirs and follows them to any device (useSyncedPref).
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { WORKERS } from '@/data/workers';
import { KBIcon } from '@/components/kbIcons';
import { useUser } from '@/auth/AuthContext';
import { useSyncedPref } from '@/hooks/useSyncedPref';
import { useGoogleActions, useGoogleStatus } from '@/hooks/useGoogleCalendar';
import { useInstall } from '@/hooks/useInstall';
import { GCHAT_DND_PREF, gchatApi } from '@/integrations/googleChatApi';
import { disablePush, enablePush, pushEnabled, pushPermission, pushSupported } from '@/lib/webPush';
import { usePhotosStatus } from '@/lib/peoplePhotos';

const T = (lang: Lang, en: string, es: string, fr: string, pt: string) =>
  lang === 'es' ? es : lang === 'fr' ? fr : lang === 'pt' ? pt : en;

/* The base every size of the portal is measured in (01-shell.css). 16 is the
   portal as it was designed; 17 for everybody was too big (Gerónimo, 29 Oct
   2026: «lo has hecho muy grande»), so it is a choice now, not the default. */
const SIZES = [15, 16, 17] as const;
type Size = (typeof SIZES)[number];

/** The text size this person chose, applied to the whole portal. */
/* Where the SYSTEM lets the browser show notices — the one switch the portal
   cannot see. */
const UA = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const BROWSER = /Edg\//.test(UA) ? 'Microsoft Edge' : /Firefox\//.test(UA) ? 'Firefox' : /Safari\//.test(UA) && !/Chrome\//.test(UA) ? 'Safari' : 'Google Chrome';
const MAC = /Mac/.test(UA);
const OS_GUIDE = MAC
  ? { en: `System Settings → Notifications → ${BROWSER} → Allow, and Focus off.`, es: `Ajustes del Sistema → Notificaciones → ${BROWSER} → Permitir, y sin modo Concentración.`, fr: `Réglages Système → Notifications → ${BROWSER} → Autoriser, sans mode Concentration.`, pt: `Definições do Sistema → Notificações → ${BROWSER} → Permitir, sem modo Foco.` }
  : { en: `Windows Settings → System → Notifications → ${BROWSER} → On.`, es: `Configuración de Windows → Sistema → Notificaciones → ${BROWSER} → Activado.`, fr: `Paramètres Windows → Système → Notifications → ${BROWSER} → Activé.`, pt: `Definições do Windows → Sistema → Notificações → ${BROWSER} → Ativado.` };

export function useTextSize(): [Size, (s: Size) => void] {
  const [size, setSize] = useSyncedPref<Size>('ui.textSize', 16);
  const px = SIZES.includes(size) ? size : 16;
  useEffect(() => {
    document.documentElement.style.fontSize = `${px}px`;
  }, [px]);
  return [px, setSize];
}

/* What each Google permission gives, said in the words of the screen it feeds. */
const SCOPE_WORDS: Record<string, [string, string, string, string]> = {
  spaces: ['Conversations', 'Conversaciones', 'Conversations', 'Conversas'],
  messages: ['Messages', 'Mensajes', 'Messages', 'Mensagens'],
  members: ['Members', 'Miembros', 'Membres', 'Membros'],
  readstate: ['Read / unread', 'Leído / sin leer', 'Lu / non lu', 'Lido / por ler'],
  names: ['Team names and photos', 'Nombres y fotos del equipo', 'Noms et photos de l’équipe', 'Nomes e fotos da equipa'],
  create: ['New conversations', 'Conversaciones nuevas', 'Nouvelles conversations', 'Conversas novas'],
  settings: ['Mute', 'Silenciar', 'Silence', 'Silenciar'],
  sections: ['Sections', 'Secciones', 'Sections', 'Secções'],
};

export function SettingsPanel({ lang, size, setSize, theme, onToggleTheme }: {
  lang: Lang; size: Size; setSize: (s: Size) => void;
  /* Light or dark lives here now, with the rest of the person's settings
     (Gerónimo, 29 Oct 2026: «el modo claro u oscuro dentro de la rueda»). */
  theme: 'light' | 'dark'; onToggleTheme: () => void;
}) {
  const { user } = useUser();
  const app = useInstall();
  /* «No molestar»: the chat's own — no cards, no bubbles, no chat notices; the
     counts still say what is unread. The same switch as the chat's bell. */
  const [dnd, setDnd] = useSyncedPref<boolean>(GCHAT_DND_PREF, false);
  /* Browser notifications, on THIS device (each browser subscribes itself). */
  type Push = 'on' | 'off' | 'blocked' | 'unsupported' | 'busy';
  const [push, setPush] = useState<Push>(() => (!pushSupported() ? 'unsupported' : pushPermission() === 'denied' ? 'blocked' : 'off'));
  useEffect(() => {
    if (!pushSupported() || pushPermission() === 'denied') return;
    let alive = true;
    void pushEnabled().then((on) => { if (alive) setPush(on ? 'on' : 'off'); });
    return () => { alive = false; };
  }, []);
  const togglePush = async () => {
    if (push === 'busy') return;
    if (push === 'on') { setPush('busy'); await disablePush(); setPush('off'); return; }
    setPush('busy');
    const r = await enablePush();
    setPush(r === 'ok' ? 'on' : r === 'denied' ? 'blocked' : 'off');
  };
  /* «Probar»: a sample notice on this device, so you see that the browser lets
     them through and what they look like. An icon on the switch's own line
     (29 Oct 2026: «rompe diseño… pon un icono y en la misma línea»). The
     browser cannot tell whether the SYSTEM showed it — macOS and Windows can
     block Chrome's notices on their own — so the line under it says where
     to look if nothing appeared. */
  const [tested, setTested] = useState(false);
  const testPush = async () => {
    const title = 'Agent Portal';
    const body = T(lang, 'This is how the portal’s notices look on this device.', 'Así se ven los avisos del portal en este dispositivo.', 'Voici les notifications du portail sur cet appareil.', 'É assim que os avisos do portal aparecem neste dispositivo.');
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (reg) await reg.showNotification(title, { body });
      else new Notification(title, { body });
      setTested(true);
    } catch { setPush('blocked'); }
  };
  const g = useGoogleStatus();
  /* The team's photos: how many came, or why none did — the one failure that
     used to say nothing (29 Oct 2026). */
  const photos = usePhotosStatus();
  /* The same query (and cache) the chat already keeps. */
  const chat = useQuery({ queryKey: ['gchat-status'], queryFn: () => gchatApi.status(), staleTime: 60_000, retry: 1 });
  const act = useGoogleActions();
  const [askOff, setAskOff] = useState(false);

  const st = g.data;
  const missing = chat.data?.missing || [];
  const me = WORKERS.find((w) => w.email?.toLowerCase() === (user?.email || '').toLowerCase());
  const when = (iso: string | null) => (iso
    ? new Date(iso).toLocaleString(lang, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '');
  const sizeName = (s: Size) => ({
    15: T(lang, 'Small', 'Pequeña', 'Petite', 'Pequena'),
    16: T(lang, 'Normal', 'Normal', 'Normale', 'Normal'),
    17: T(lang, 'Large', 'Grande', 'Grande', 'Grande'),
  })[s];

  return (
    <div className="tb-set">
      {/* The WHOLE portal grows or shrinks with it — every size is measured
          in this one: letters, spacing, buttons (Gerónimo, 29 Oct 2026: «el
          tamaño global del portal»). */}
      <div className="tb-menu-label">{T(lang, 'Portal size', 'Tamaño del portal', 'Taille du portail', 'Tamanho do portal')}</div>
      <div className="tb-set-sizes" role="radiogroup" aria-label={T(lang, 'Portal size', 'Tamaño del portal', 'Taille du portail', 'Tamanho do portal')}>
        {SIZES.map((s) => (
          <button key={s} type="button" role="radio" aria-checked={size === s}
            className={`tb-set-size ${size === s ? 'on' : ''}`} onClick={() => setSize(s)}>
            <b style={{ fontSize: `${s - 2}px` }}>Aa</b>
            <span>{sizeName(s)}</span>
          </button>
        ))}
      </div>

      <p className="tb-set-note sub">{T(lang, 'Text, spacing and buttons, all at once.', 'La letra, los espacios y los botones, todo a la vez.', 'Le texte, les espaces et les boutons, tout à la fois.', 'O texto, os espaços e os botões, tudo de uma vez.')}</p>

      <div className="tb-menu-label">{T(lang, 'Appearance', 'Apariencia', 'Apparence', 'Aparência')}</div>
      <div className="tb-set-seg" role="radiogroup" aria-label={T(lang, 'Appearance', 'Apariencia', 'Apparence', 'Aparência')}>
        {(['light', 'dark'] as const).map((th) => (
          <button key={th} type="button" role="radio" aria-checked={theme === th}
            className={`tb-set-segopt ${theme === th ? 'on' : ''}`} onClick={() => { if (theme !== th) onToggleTheme(); }}>
            <KBIcon name={th === 'dark' ? 'moon' : 'sun'} size={14} />
            {th === 'dark' ? T(lang, 'Dark', 'Oscuro', 'Sombre', 'Escuro') : T(lang, 'Light', 'Claro', 'Clair', 'Claro')}
          </button>
        ))}
      </div>

      <div className="tb-menu-label">{T(lang, 'Notices', 'Avisos', 'Notifications', 'Avisos')}</div>
      <div className="tb-set-conn">
        <div className="tb-set-row">
          <button type="button" role="switch" aria-checked={push === 'on'} className={`tb-set-toggle ${push === 'on' ? 'has-tool' : ''}`}
            disabled={push === 'unsupported' || push === 'blocked' || push === 'busy'} onClick={() => void togglePush()}>
            <span>
              <b>{T(lang, 'Browser notifications', 'Avisos del navegador', 'Notifications du navigateur', 'Avisos do navegador')}</b>
              <em>{push === 'unsupported'
                ? T(lang, 'This browser cannot show them.', 'Este navegador no puede mostrarlos.', 'Ce navigateur ne peut pas les afficher.', 'Este navegador não os consegue mostrar.')
                : push === 'blocked'
                  ? T(lang, 'Blocked in the browser: padlock in the address bar → Notifications → Allow.', 'Bloqueados en el navegador: candado de la barra de direcciones → Notificaciones → Permitir.', 'Bloquées : cadenas de la barre d’adresse → Notifications → Autoriser.', 'Bloqueados: cadeado da barra de endereço → Notificações → Permitir.')
                  : push === 'on' && tested
                    ? T(lang, `Sent. Not on your screen? ${OS_GUIDE.en}`, `Enviado. ¿No te ha salido? ${OS_GUIDE.es}`, `Envoyée. Rien à l’écran ? ${OS_GUIDE.fr}`, `Enviado. Não apareceu? ${OS_GUIDE.pt}`)
                    : T(lang, 'On this device, even with the portal closed.', 'En este dispositivo, también con el portal cerrado.', 'Sur cet appareil, même portail fermé.', 'Neste dispositivo, mesmo com o portal fechado.')}</em>
            </span>
            <i className={`tb-set-switch ${push === 'on' ? 'on' : ''}`} aria-hidden="true" />
          </button>
          {push === 'on' && (
            <button type="button" className={`tb-set-test ${tested ? 'done' : ''}`} onClick={() => void testPush()}
              data-tip={T(lang, 'Send a test notice', 'Enviar un aviso de prueba', 'Envoyer une notification de test', 'Enviar um aviso de teste')}
              aria-label={T(lang, 'Send a test notice', 'Enviar un aviso de prueba', 'Envoyer une notification de test', 'Enviar um aviso de teste')}>
              <KBIcon name={tested ? 'check' : 'bell'} size={14} />
            </button>
          )}
        </div>
        <button type="button" role="switch" aria-checked={dnd} className="tb-set-toggle" onClick={() => setDnd(!dnd)}>
          <span>
            <b>{T(lang, 'Do not disturb', 'No molestar', 'Ne pas déranger', 'Não incomodar')}</b>
            <em>{T(lang, 'No chat pop-ups, bubbles or notices. The counters still say what is unread.', 'Sin ventanitas, burbujas ni avisos del chat. Los contadores siguen diciendo lo que hay sin leer.', 'Ni fenêtres, ni bulles, ni notifications du chat. Les compteurs restent.', 'Sem janelas, bolhas nem avisos do chat. Os contadores continuam.')}</em>
          </span>
          <i className={`tb-set-switch ${dnd ? 'on' : ''}`} aria-hidden="true" />
        </button>
      </div>

      <div className="tb-menu-label">{T(lang, 'Desktop app', 'App de escritorio', 'Application de bureau', 'App de computador')}</div>
      <div className="tb-set-conn">
        <p className="tb-set-note">
          {app.state === 'installed'
            ? T(lang, 'Installed on this device — open it from its icon.', 'Instalada en este dispositivo: ábrela desde su icono.', 'Installée sur cet appareil : ouvrez-la depuis son icône.', 'Instalada neste dispositivo: abra-a pelo ícone.')
            : app.state === 'available'
              ? T(lang, 'Its own window and icon, and notifications with the portal closed.', 'Ventana e icono propios, y avisos con el portal cerrado.', 'Fenêtre et icône propres, et notifications portail fermé.', 'Janela e ícone próprios, e avisos com o portal fechado.')
              : app.state === 'manual'
                ? T(lang, 'In Safari: File → Add to Dock (Mac), or Share → Add to Home Screen (iPhone).', 'En Safari: Archivo → Añadir al Dock (Mac), o Compartir → Añadir a pantalla de inicio (iPhone).', 'Dans Safari : Fichier → Ajouter au Dock (Mac), ou Partager → Sur l’écran d’accueil (iPhone).', 'No Safari: Ficheiro → Adicionar à Dock (Mac), ou Partilhar → Ecrã principal (iPhone).')
                : app.state === 'declined'
                  ? T(lang, 'Install it any time from the ⊕ icon at the right of the address bar.', 'Instálala cuando quieras desde el icono ⊕ a la derecha de la barra de direcciones.', 'Installez-la depuis l’icône ⊕ à droite de la barre d’adresse.', 'Instale-a pelo ícone ⊕ à direita da barra de endereço.')
                  : app.state === 'probably'
                    ? T(lang, 'Chrome only offers it when you do not have it — open it from your apps.', 'Chrome solo la ofrece cuando no la tienes: ábrela desde tus aplicaciones.', 'Chrome ne la propose que si vous ne l’avez pas : ouvrez-la depuis vos applications.', 'O Chrome só a oferece quando não a tem: abra-a pelas suas aplicações.')
                    : T(lang, 'Open the portal in Chrome or Edge to install it in one click.', 'Abre el portal en Chrome o Edge para instalarla de un clic.', 'Ouvrez le portail dans Chrome ou Edge pour l’installer en un clic.', 'Abra o portal no Chrome ou no Edge para a instalar num clique.')}
        </p>
        {app.state === 'available' && (
          <div className="tb-set-acts">
            <button type="button" className="tb-set-btn primary" onClick={() => void app.install()}>{T(lang, 'Install the app', 'Instalar la app', 'Installer l’application', 'Instalar a app')}</button>
          </div>
        )}
      </div>

      <div className="tb-menu-label">{T(lang, 'Connections', 'Conexiones', 'Connexions', 'Ligações')}</div>

      <div className="tb-set-conn">
        <div className="tb-set-row">
          <i className={`tb-set-dot ${!st?.configured ? 'off' : st.connected && !missing.length ? 'ok' : 'warn'}`} />
          <b>Google</b>
          <em>Chat · Calendar</em>
        </div>
        {g.isLoading ? (
          <p className="tb-set-note">…</p>
        ) : !st?.configured ? (
          <p className="tb-set-note">{T(lang, 'Google is not switched on for this portal yet.', 'Google aún no está activado en este portal.', 'Google n’est pas encore activé sur ce portail.', 'O Google ainda não está ativado neste portal.')}</p>
        ) : !st.connected ? (
          <>
            <p className="tb-set-note">{T(lang, 'Not connected: your Chat and Calendar are not in the portal.', 'Sin conectar: tu Chat y tu Calendar no están en el portal.', 'Non connecté : votre Chat et votre agenda ne sont pas dans le portail.', 'Sem ligação: o seu Chat e o seu Calendar não estão no portal.')}</p>
            <button type="button" className="tb-set-btn primary" onClick={() => act.connect.mutate()}>
              {T(lang, 'Connect Google', 'Conectar Google', 'Connecter Google', 'Ligar Google')}
            </button>
          </>
        ) : (
          <>
            <p className="tb-set-note">
              {T(lang, 'Connected as', 'Conectado como', 'Connecté en tant que', 'Ligado como')} <b>{st.googleEmail}</b>
              {st.lastSyncAt && <><br />{T(lang, 'Last sync', 'Última sincronización', 'Dernière synchro', 'Última sincronização')}: {when(st.lastSyncAt)}</>}
            </p>
            {missing.length > 0 && (
              <p className="tb-set-note warn">
                {T(lang, 'Missing permissions', 'Faltan permisos', 'Autorisations manquantes', 'Faltam permissões')}: {missing.map((k) => {
                  const w = SCOPE_WORDS[k];
                  return w ? T(lang, ...w) : k;
                }).join(', ')}. {T(lang, 'Reconnect and leave every box ticked.', 'Reconecta y deja todas las casillas marcadas.', 'Reconnectez et laissez toutes les cases cochées.', 'Volte a ligar e deixe todas as caixas marcadas.')}
              </p>
            )}
            {st.lastError && <p className="tb-set-note warn">⚠ {st.lastError}</p>}
            {!photos.loading && (photos.n > 0
              ? <p className="tb-set-note">{T(lang, 'Team photos', 'Fotos del equipo', 'Photos de l’équipe', 'Fotos da equipa')}: {photos.n}</p>
              : <p className="tb-set-note warn">{T(lang, 'Team photos: none came', 'Fotos del equipo: no llega ninguna', 'Photos de l’équipe : aucune', 'Fotos da equipa: nenhuma')}{photos.why ? ` — ${photos.why}` : ''}</p>)}
            {askOff ? (
              <div className="tb-set-acts">
                <span className="tb-set-ask">{T(lang, 'Chat and Calendar stop working here.', 'Chat y Calendar dejan de funcionar aquí.', 'Chat et l’agenda s’arrêtent ici.', 'O Chat e o Calendar deixam de funcionar aqui.')}</span>
                <button type="button" className="tb-set-btn" onClick={() => setAskOff(false)}>{T(lang, 'Cancel', 'Cancelar', 'Annuler', 'Cancelar')}</button>
                <button type="button" className="tb-set-btn bad" disabled={act.disconnect.isPending}
                  onClick={() => act.disconnect.mutate(undefined, { onSettled: () => setAskOff(false) })}>
                  {T(lang, 'Disconnect', 'Desconectar', 'Déconnecter', 'Desligar')}
                </button>
              </div>
            ) : (
              <div className="tb-set-acts">
                <button type="button" className={`tb-set-btn ${missing.length ? 'primary' : ''}`} onClick={() => act.connect.mutate()}>
                  {T(lang, 'Reconnect', 'Reconectar', 'Reconnecter', 'Voltar a ligar')}
                </button>
                <button type="button" className="tb-set-btn" onClick={() => setAskOff(true)}>
                  {T(lang, 'Disconnect', 'Desconectar', 'Déconnecter', 'Desligar')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="tb-set-conn">
        <div className="tb-set-row">
          <i className={`tb-set-dot ${me?.hubspotOwnerId ? 'ok' : 'warn'}`} />
          <b>HubSpot</b>
        </div>
        <p className="tb-set-note">
          {me?.hubspotOwnerId
            ? T(lang, 'Linked: your HubSpot tickets and tasks show as yours.', 'Enlazado: tus tickets y tareas de HubSpot salen como tuyos.', 'Lié : vos tickets et tâches HubSpot apparaissent comme les vôtres.', 'Ligado: os seus tickets e tarefas do HubSpot aparecem como seus.')
            : T(lang, 'Not linked: your HubSpot tickets do not show as yours. Ask a portal admin to link your HubSpot user.', 'Sin enlazar: tus tickets de HubSpot no salen como tuyos. Pide a un administrador del portal que enlace tu usuario de HubSpot.', 'Non lié : vos tickets HubSpot n’apparaissent pas comme les vôtres. Demandez à un administrateur du portail de lier votre utilisateur HubSpot.', 'Sem ligação: os seus tickets do HubSpot não aparecem como seus. Peça a um administrador do portal para ligar o seu utilizador do HubSpot.')}
        </p>
      </div>
    </div>
  );
}
